import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';
import WinStateModal from './WinStateModal';

const ProofPhotoUpload = ({ duelId, userId, onPhotoUploaded, duelTitle }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStage, setUploadStage] = useState(''); // 'uploading', 'saving', 'done'
  const [preview, setPreview] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [showWinState, setShowWinState] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;

    if (!file?.type?.startsWith('image/')) {
      setError('Por favor selecciona una imagen válida');
      return;
    }

    if (file?.size > 5 * 1024 * 1024) {
      setError('La imagen debe ser menor a 5MB');
      return;
    }

    setError(null);
    setSelectedFile(file);

    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader?.result);
    };
    reader?.readAsDataURL(file);
  };

  const simulateProgress = (from, to, duration, onDone) => {
    const steps = 20;
    const increment = (to - from) / steps;
    const interval = duration / steps;
    let current = from;
    const timer = setInterval(() => {
      current += increment;
      if (current >= to) {
        current = to;
        clearInterval(timer);
        onDone?.();
      }
      setUploadProgress(Math.round(current));
    }, interval);
    return timer;
  };

  const handleUpload = async (fileOverride) => {
    const file = fileOverride || selectedFile || fileInputRef?.current?.files?.[0];
    if (!file) return;

    setUploading(true);
    setError(null);
    setUploadProgress(0);
    setUploadStage('uploading');

    try {
      const fileExt = file?.name?.split('.')?.pop();
      const filePath = `${userId}/${duelId}_${Date.now()}.${fileExt}`;

      // Simulate upload progress from 0→80 while actual upload runs
      const progressTimer = simulateProgress(0, 80, 1800, null);

      const { data: uploadData, error: uploadError } = await supabase?.storage
        ?.from('duel-proofs')
        ?.upload(filePath, file, { cacheControl: '3600', upsert: false });

      clearInterval(progressTimer);

      if (uploadError) throw uploadError;

      setUploadProgress(85);
      setUploadStage('saving');

      const { data: urlData } = supabase?.storage?.from('duel-proofs')?.getPublicUrl(filePath);

      const { data: proofData, error: proofError } = await supabase
        ?.from('duel_proofs')
        ?.insert({
          duel_id: duelId,
          user_id: userId,
          proof_date: new Date()?.toISOString()?.split('T')?.[0],
          proof_value: 1,
          proof_type: 'photo',
          photo_url: urlData?.publicUrl,
          auto_verified: false,
          notes: 'Foto de verificación subida por el usuario'
        })
        ?.select()
        ?.single();

      if (proofError) throw proofError;

      setUploadProgress(100);
      setUploadStage('done');

      setTimeout(() => {
        setPreview(null);
        setSelectedFile(null);
        setUploadProgress(0);
        setUploadStage('');
        if (fileInputRef?.current) fileInputRef.current.value = '';
        setShowWinState(true);
        onPhotoUploaded?.(proofData);
      }, 600);
    } catch (err) {
      console.error('Error uploading proof photo:', err);
      setError(err?.message || 'Error al subir la foto. Intenta de nuevo.');
      setUploadProgress(0);
      setUploadStage('');
    } finally {
      setUploading(false);
    }
  };

  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
    setError(null);
    handleUpload(selectedFile);
  };

  const handleCancel = () => {
    setPreview(null);
    setSelectedFile(null);
    setError(null);
    setUploadProgress(0);
    setUploadStage('');
    if (fileInputRef?.current) fileInputRef.current.value = '';
  };

  const stageLabel = {
    uploading: 'Subiendo imagen...',
    saving: 'Guardando registro...',
    done: '¡Completado!'
  };

  return (
    <>
      <div className="space-y-4">
        <div className="p-4 bg-primary/5 rounded-xl border border-primary/20">
          <div className="flex items-start space-x-3 mb-3">
            <Icon name="Camera" size={24} className="text-primary mt-1" />
            <div>
              <h4 className="text-sm font-semibold text-foreground mb-1">
                Verificación con Foto
              </h4>
              <p className="text-xs text-muted-foreground">
                Sube una foto que demuestre que estás completando el duelo (ej: foto en el gym, comida saludable, etc.)
              </p>
            </div>
          </div>

          {!preview ? (
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
                id="proof-photo-input"
              />
              <label htmlFor="proof-photo-input">
                <div className="border-2 border-dashed border-border rounded-lg p-6 text-center cursor-pointer hover:border-primary/50 hover:bg-primary/5 transition-colors">
                  <Icon name="Upload" size={32} className="text-muted-foreground mx-auto mb-2" />
                  <p className="text-sm font-medium text-foreground mb-1">
                    Haz clic para seleccionar una foto
                  </p>
                  <p className="text-xs text-muted-foreground">
                    JPG, PNG o GIF (máx. 5MB)
                  </p>
                </div>
              </label>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Preview with spinner overlay */}
              <div className="relative rounded-lg overflow-hidden">
                <img
                  src={preview}
                  alt="Vista previa de la foto de verificación"
                  className={`w-full h-48 object-cover transition-opacity duration-300 ${
                    uploading ? 'opacity-50' : 'opacity-100'
                  }`}
                />
                {uploading && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/40 backdrop-blur-sm">
                    <div className="w-12 h-12 rounded-full border-4 border-white/30 border-t-white animate-spin mb-3" />
                    <p className="text-white text-xs font-semibold tracking-wide">
                      {stageLabel?.[uploadStage] || 'Procesando...'}
                    </p>
                  </div>
                )}
                {uploadStage === 'done' && (
                  <div className="absolute inset-0 flex items-center justify-center bg-success/30 backdrop-blur-sm">
                    <div className="w-14 h-14 rounded-full bg-success flex items-center justify-center shadow-lg">
                      <Icon name="Check" size={28} className="text-white" />
                    </div>
                  </div>
                )}
              </div>

              {/* Progress bar */}
              {uploading && (
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">{stageLabel?.[uploadStage]}</span>
                    <span className="text-xs font-semibold text-primary">{uploadProgress}%</span>
                  </div>
                  <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-primary to-primary/70 rounded-full transition-all duration-300 ease-out"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              )}

              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCancel}
                  disabled={uploading}
                  fullWidth
                >
                  <Icon name="X" size={16} className="mr-1" />
                  Cancelar
                </Button>
                <Button
                  size="sm"
                  onClick={() => handleUpload()}
                  loading={uploading}
                  disabled={uploading}
                  fullWidth
                >
                  <Icon name="Upload" size={16} className="mr-1" />
                  Subir Foto
                </Button>
              </div>
            </div>
          )}

          {/* Error with retry */}
          {error && (
            <div className="mt-3 p-3 bg-error/10 border border-error/30 rounded-lg">
              <div className="flex items-start justify-between space-x-2">
                <div className="flex items-start space-x-2 flex-1">
                  <Icon name="AlertCircle" size={16} className="text-error mt-0.5 shrink-0" />
                  <p className="text-xs text-error">{error}</p>
                </div>
                {selectedFile && (
                  <button
                    onClick={handleRetry}
                    className="flex items-center space-x-1 text-xs font-semibold text-error hover:text-error/80 transition-colors shrink-0 ml-2"
                  >
                    <Icon name="RefreshCw" size={13} />
                    <span>Reintentar{retryCount > 0 ? ` (${retryCount})` : ''}</span>
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      <WinStateModal
        isOpen={showWinState}
        onClose={() => setShowWinState(false)}
        duelTitle={duelTitle}
      />
    </>
  );
};

export default ProofPhotoUpload;